"use client";

import { MenuIcon } from "@/client/components/common/Nav/icons";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuStore,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/client/components/common/NavigationMenu";
import {
  ArtBlocksWordmarkLockupFullColor,
  LilSquigFullColor,
} from "@/client/components/icons/ab-logo";
import { cn } from "@/client/lib/classnames";
import { AB_FLAGSHIP_URL } from "@/client/lib/links";
import {
  GetAllLiveArticlePagesQuery,
  GetAllLiveExhibitionPagesQuery,
} from "@/gql/graphql";
import { ArtistItemInfo } from "@/shared/nav";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
} from "motion/react";
import Link from "next/link";
import React, { ComponentProps, forwardRef, useEffect } from "react";
import AccountNavigationMenuItem from "./AccountNavigationItem";
import { NAV_ITEMS } from "./constants";
import { FullSiteSearch } from "./FullSiteSearch";
import { ResponsiveMenu } from "./ResponsiveMenu";

type NavProps = {
  artistData: ArtistItemInfo[] | undefined;
  articleData: GetAllLiveArticlePagesQuery | undefined;
  exhibitionData: GetAllLiveExhibitionPagesQuery | undefined;
};

export const Nav = ({
  articleData,
  artistData,
  exhibitionData,
}: NavProps): React.ReactElement | null => {
  const [fullWidthSearch, setFullWidthSearch] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState<boolean>(false);

  // Logic for hiding the nav bar when scrolling up
  const [navHidden, setNavHidden] = React.useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    const threshold = 85; // Arbitrary threshold for smoothness
    if (previous && previous < latest && latest > threshold) {
      setNavHidden(true);
    } else {
      setNavHidden(false);
    }
  });

  useEffect(() => {
    const navbar = document.querySelector("#navbar");
    if (!navbar) return;

    const updateNavHeight = (height: number) => {
      document.documentElement.style.setProperty(
        "--nav-bar-height",
        `${height}px`
      );
    };

    // Set initial height
    updateNavHeight(navbar.getBoundingClientRect().height);

    // Watch for changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateNavHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(navbar);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <ResponsiveMenu visible={showMenu} onDismiss={() => setShowMenu(false)} />
      <motion.div
        className="sticky top-0 z-30 w-full"
        id="navbar"
        animate={{
          y: navHidden ? "-100%" : "0%",
        }}
        transition={{
          bounce: 0,
          visualDuration: 0.4,
        }}
      >
        <motion.div
          className={`p-md bg-surface gap-md flex flex-row items-center`}
          animate={{ gap: fullWidthSearch ? 0 : "24px" }}
        >
          <AnimatePresence initial={false}>
            {!fullWidthSearch ? (
              <motion.div
                className={cn(
                  "gap-x-md flex flex-row items-center overflow-hidden"
                )}
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
              >
                <Link href={AB_FLAGSHIP_URL}>
                  <ArtBlocksWordmarkLockupFullColor className="fill-on-surface hidden h-[30px] sm:block" />
                  <LilSquigFullColor className="fill-on-surface block h-[30px] sm:hidden" />
                </Link>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <motion.div className="flex-1">
            <FullSiteSearch
              articleData={articleData}
              artistData={artistData}
              exhibitionData={exhibitionData}
              onFocus={() => {
                if (window.innerWidth >= 640) {
                  return;
                }
                setFullWidthSearch(true);
              }}
              onBlur={() => {
                setFullWidthSearch(false);
              }}
            />
          </motion.div>
          <AnimatePresence initial={false}>
            {!fullWidthSearch ? (
              <motion.div
                className="flex gap-x-xs"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
              >
                <NavigationMenu>
                  <NavigationMenuList>
                    {NAV_ITEMS.map((item) => {
                      return (
                        <NavigationMenuItem
                          key={item.value}
                          className="relative hidden lg:block"
                          value={item.value}
                        >
                          {item.subItems ? (
                            <NavigationMenuTrigger asChild>
                              <NavigationMenuAllPurposeLink
                                href={item.href ?? ""}
                                className="hover:bg-surface data-[active]:bg-surface data-[state=open]:bg-surface"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                  }
                                  navigationMenuStore.send({
                                    type: "setActiveItem",
                                    activeItem: item.value,
                                  });
                                }}
                              >
                                {item.label}
                              </NavigationMenuAllPurposeLink>
                            </NavigationMenuTrigger>
                          ) : (
                            <NavigationMenuAllPurposeLink
                              href={item.href ?? ""}
                              className="hover:bg-surface data-[active]:bg-surface data-[state=open]:bg-surface"
                            >
                              {item.label}
                            </NavigationMenuAllPurposeLink>
                          )}
                          {item.subItems ? (
                            <NavigationMenuContent
                              className="min-w-max"
                              contentFor={item.value}
                            >
                              {item.subItems.map((subItem) => {
                                return (
                                  <NavigationMenuAllPurposeLink
                                    key={subItem.value}
                                    href={subItem.href ?? ""}
                                    className="block w-auto"
                                  >
                                    {subItem.label}
                                  </NavigationMenuAllPurposeLink>
                                );
                              })}
                            </NavigationMenuContent>
                          ) : null}
                        </NavigationMenuItem>
                      );
                    })}
                    <AccountNavigationMenuItem />
                  </NavigationMenuList>
                </NavigationMenu>
                <button
                  className={cn(
                    navigationMenuTriggerStyle(),
                    "block lg:hidden"
                  )}
                  onClick={() => {
                    setShowMenu(!showMenu);
                  }}
                >
                  <MenuIcon className={"fill-on-surface w-sm h-sm"} />
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  );
};

export const NavigationMenuAllPurposeLink = forwardRef<
  HTMLAnchorElement,
  ComponentProps<typeof Link>
>(({ href, children, className, ...props }, ref) => {
  return (
    <NavigationMenuLink
      className={cn("w-full justify-start text-left", className)}
      asChild
    >
      <Link ref={ref} href={href} {...props}>
        <span className="text-body">{children}</span>
      </Link>
    </NavigationMenuLink>
  );
});
NavigationMenuAllPurposeLink.displayName = "NavigationMenuAllPurposeLink";
